import { useContext, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { ACTIVE_TOURNAMENT } from '@/constants';
import { AI_MODEL_CONTENT } from '@/constants/aiLeague/aiLeagueContent';
import { FirebaseContext } from '@/session/FirebaseContext';
import { mergeAiLeagueModels } from '@/helpers/leagues/mergeAiLeagueModels';
import type { AiModelContent } from '@/constants/aiLeague/aiLeagueTypes';
import type { AiLeagueSwapRecord, AiLeagueSwapsById } from '@/models/aiLeague';
import type { AiModelId } from '@/constants/aiLeague/aiLeagueTypes';

/**
 * Merges bundled AI League content with Firestore swap overlays under
 * `competitions/{tid}/aiLeague/*` (signed-in read). Guests keep static lists; `swapsReady`
 * may stay false if the subscription fails without auth.
 */
export function useAiLeagueModels(): {
  aiModels: AiModelContent[];
  swapsById: AiLeagueSwapsById;
  swapsReady: boolean;
} {
  const ctx = useContext(FirebaseContext);
  const [swapsById, setSwapsById] = useState<AiLeagueSwapsById>({});
  const [swapsReady, setSwapsReady] = useState(false);

  useEffect(() => {
    return onSnapshot(
      collection(ctx.db, 'competitions', ACTIVE_TOURNAMENT, 'aiLeague'),
      (snap) => {
        const next: AiLeagueSwapsById = {};
        for (const docSnap of snap.docs) {
          next[docSnap.id as AiModelId] = docSnap.data() as AiLeagueSwapRecord;
        }
        setSwapsById(next);
        setSwapsReady(true);
      },
      () => {
        setSwapsById({});
        setSwapsReady(true);
      },
    );
  }, [ctx.db]);

  const aiModels = useMemo(
    () => mergeAiLeagueModels(AI_MODEL_CONTENT, swapsById),
    [swapsById],
  );

  return { aiModels, swapsById, swapsReady };
}
